<template>
  <div v-if="visible" class="beauty-modal-overlay" @click="handleOverlayClick">
    <div class="beauty-modal" :class="type" @click.stop>
      <!-- 背景装饰 -->
      <div class="modal-bg-decoration"></div>
      
      <!-- 图标区域 -->
      <div class="modal-icon-container">
        <div class="icon-wrapper" :class="type">
          <icon-check-circle v-if="type === 'success'" class="modal-icon success"/>
          <icon-info-circle v-else-if="type === 'info'" class="modal-icon info"/>
          <icon-exclamation-circle v-else-if="type === 'warning'" class="modal-icon warning"/>
          <icon-close-circle v-else-if="type === 'error'" class="modal-icon error"/>
          <icon-send v-else class="modal-icon default"/>
        </div>
      </div>
      
      <!-- 内容区域 -->
      <div class="modal-content">
        <h3 class="modal-title">{{ title }}</h3>
        <p class="modal-message">{{ message }}</p>
        
        <!-- 额外信息 -->
        <div v-if="extraInfo" class="modal-extra-info">
          <icon-info-circle class="extra-icon"/>
          <span>{{ extraInfo }}</span>
        </div>
      </div>
      
      <!-- 按钮区域 -->
      <div class="modal-actions">
        <button 
          v-if="showCancel" 
          class="modal-btn cancel-btn" 
          @click="handleCancel"
        >
          {{ cancelText }}
        </button>
        <button 
          class="modal-btn confirm-btn" 
          :class="type"
          @click="handleConfirm"
        >
          <span class="btn-text">{{ confirmText }}</span>
          <div class="btn-shine"></div>
        </button>
      </div>
      
      <!-- 关闭按钮 -->
      <button class="modal-close" @click="handleClose">
        <icon-close class="close-icon"/>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BeautyModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'info',
      validator: value => ['success', 'info', 'warning', 'error', 'default'].includes(value)
    },
    title: {
      type: String,
      default: '提示'
    },
    message: {
      type: String,
      default: ''
    },
    extraInfo: {
      type: String,
      default: ''
    },
    confirmText: {
      type: String,
      default: '确定'
    },
    cancelText: {
      type: String,
      default: '取消'
    },
    showCancel: {
      type: Boolean,
      default: false
    },
    closeOnOverlay: {
      type: Boolean,
      default: true
    }
  },
  emits: ['confirm', 'cancel', 'close'],
  methods: {
    handleConfirm() {
      this.$emit('confirm');
    },
    handleCancel() {
      this.$emit('cancel');
    },
    handleClose() {
      this.$emit('close');
    },
    handleOverlayClick() {
      if (this.closeOnOverlay) {
        this.handleClose();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.beauty-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: modalFadeIn 0.3s ease-out;
}

.beauty-modal {
  position: relative;
  width: 90%;
  max-width: 420px;
  background: var(--background-primary);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-strength-100);
  border-radius: 24px;
  padding: 40px 30px 30px;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.3),
    0 0 0 1px var(--border-strength-100);
  animation: modalSlideIn 0.4s ease-out;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
    border-radius: 24px;
    z-index: -1;
  }
  
  &.success {
    border-color: rgba(16, 185, 129, 0.3);
    
    &::before {
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%);
    }
  }
  
  &.warning {
    border-color: rgba(245, 158, 11, 0.3);
    
    &::before {
      background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%);
    }
  }
  
  &.error {
    border-color: rgba(239, 68, 68, 0.3);
    
    &::before {
      background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
    }
  }
}

.modal-bg-decoration {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: 
    radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%);
  animation: modalBgRotate 20s linear infinite;
  z-index: -2;
}

.modal-icon-container {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  
  .icon-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.2);
    animation: iconPulse 2s ease-in-out infinite;
    
    &::before {
      content: '';
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%);
      z-index: -1;
      animation: iconGlow 3s ease-in-out infinite;
    }
    
    &.success {
      border-color: rgba(16, 185, 129, 0.4);
      
      &::before {
        background: linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.3) 100%);
      }
    }
    
    &.warning {
      border-color: rgba(245, 158, 11, 0.4);
      
      &::before {
        background: linear-gradient(135deg, rgba(245, 158, 11, 0.3) 0%, rgba(217, 119, 6, 0.3) 100%);
      }
    }
    
    &.error {
      border-color: rgba(239, 68, 68, 0.4);
      
      &::before {
        background: linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(220, 38, 38, 0.3) 100%);
      }
    }
    
    .modal-icon {
      font-size: 36px;
      color: #fff;
      
      &.success {
        color: #10b981;
      }
      
      &.info {
        color: #3b82f6;
      }
      
      &.warning {
        color: #f59e0b;
      }
      
      &.error {
        color: #ef4444;
      }
      
      &.default {
        color: #667eea;
      }
    }
  }
}

.modal-content {
  text-align: center;
  margin-bottom: 25px;
  
  .modal-title {
    font-size: 24px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 12px 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .modal-message {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.6;
    margin: 0 0 15px 0;
  }
  
  .modal-extra-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    
    .extra-icon {
      font-size: 16px;
      color: #f59e0b;
    }
  }
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  
  .modal-btn {
    flex: 1;
    height: 44px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    
    .btn-text {
      position: relative;
      z-index: 2;
    }
    
    .btn-shine {
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s ease;
    }
    
    &:hover .btn-shine {
      left: 100%;
    }
    
    &.cancel-btn {
      background: rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.2);
      
      &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: translateY(-1px);
      }
    }
    
    &.confirm-btn {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
      }
      
      &.success {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        
        &:hover {
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
        }
      }
      
      &.warning {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        
        &:hover {
          box-shadow: 0 8px 25px rgba(245, 158, 11, 0.4);
        }
      }
      
      &.error {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        
        &:hover {
          box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
        }
      }
    }
  }
}

.modal-close {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
  
  .close-icon {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.7);
  }
}

/* 动画效果 */
@keyframes modalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes modalBgRotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes iconGlow {
  0%, 100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1);
  }
}

/* 响应式设计 */
@media (max-width: 480px) {
  .beauty-modal {
    width: 95%;
    padding: 30px 20px 25px;
    
    .modal-icon-container .icon-wrapper {
      width: 60px;
      height: 60px;
      
      .modal-icon {
        font-size: 28px;
      }
    }
    
    .modal-content {
      .modal-title {
        font-size: 20px;
      }
      
      .modal-message {
        font-size: 14px;
      }
    }
    
    .modal-actions {
      flex-direction: column;
      
      .modal-btn {
        height: 40px;
        font-size: 14px;
      }
    }
  }
}

/* 黑夜模式特殊优化 */
[data-theme="dark"] .beauty-modal-overlay {
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(12px);
}

[data-theme="dark"] .beauty-modal {
  background: rgba(30, 30, 30, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    0 0 20px rgba(102, 126, 234, 0.2);
  
  &::before {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%);
  }
  
  &.success {
    border-color: rgba(16, 185, 129, 0.4);
    box-shadow: 
      0 25px 50px rgba(0, 0, 0, 0.5),
      0 0 0 1px rgba(16, 185, 129, 0.2),
      0 0 20px rgba(16, 185, 129, 0.3);
    
    &::before {
      background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%);
    }
  }
  
  &.warning {
    border-color: rgba(245, 158, 11, 0.4);
    box-shadow: 
      0 25px 50px rgba(0, 0, 0, 0.5),
      0 0 0 1px rgba(245, 158, 11, 0.2),
      0 0 20px rgba(245, 158, 11, 0.3);
    
    &::before {
      background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%);
    }
  }
  
  &.error {
    border-color: rgba(239, 68, 68, 0.4);
    box-shadow: 
      0 25px 50px rgba(0, 0, 0, 0.5),
      0 0 0 1px rgba(239, 68, 68, 0.2),
      0 0 20px rgba(239, 68, 68, 0.3);
    
    &::before {
      background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%);
    }
  }
}

[data-theme="dark"] .modal-icon-container .icon-wrapper {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  
  &::before {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%);
  }
  
  &.success::before {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.4) 0%, rgba(5, 150, 105, 0.4) 100%);
  }
  
  &.warning::before {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.4) 0%, rgba(217, 119, 6, 0.4) 100%);
  }
  
  &.error::before {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.4) 0%, rgba(220, 38, 38, 0.4) 100%);
  }
}

[data-theme="dark"] .modal-content {
  .modal-title {
    color: #fff;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
  
  .modal-message {
    color: rgba(255, 255, 255, 0.95);
  }
  
  .modal-extra-info {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
  }
}

[data-theme="dark"] .modal-actions {
  .modal-btn {
    &.cancel-btn {
      background: rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.1);
      
      &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.2);
      }
    }
    
    &.confirm-btn {
      &.success {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
        
        &:hover {
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.5);
        }
      }
      
      &.warning {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
        
        &:hover {
          box-shadow: 0 8px 25px rgba(245, 158, 11, 0.5);
        }
      }
      
      &.error {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
        
        &:hover {
          box-shadow: 0 8px 25px rgba(239, 68, 68, 0.5);
        }
      }
    }
  }
}

[data-theme="dark"] .modal-close {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  .close-icon {
    color: rgba(255, 255, 255, 0.8);
  }
}
</style> 